/**
 * 同步服务 - 负责将SOS记录上报到服务器
 * 使用Background Sync API实现离线同步
 */

import type { SOSRecord, EmergencyReportRequest, ApiConfig } from '../types';

// API配置 - 待对接警方/团圆系统
const API_CONFIG: ApiConfig = {
  baseUrl: 'https://api.mock-sos.example.com', // Mock API占位
  timeout: 10000,
  retryCount: 3,
  retryDelay: 30000
};

/**
 * 发送SOS紧急求助到服务器
 * @param record SOS记录
 * @returns 上报结果
 */
export async function reportEmergency(record: SOSRecord): Promise<{ success: boolean; message: string }> {
  const encryptedPayload: {
    location: string;
    locationIV: string;
    photo?: string;
    photoIV?: string;
    description?: string;
    descriptionIV?: string;
  } = {
    location: record.encryptedLocation,
    locationIV: record.encryptedLocationIV
  };

  if (record.encryptedPhoto) {
    encryptedPayload.photo = record.encryptedPhoto;
    encryptedPayload.photoIV = record.encryptedPhotoIV!;
  }

  if (record.encryptedDescription) {
    encryptedPayload.description = record.encryptedDescription;
    encryptedPayload.descriptionIV = record.encryptedDescriptionIV!;
  }

  const request: EmergencyReportRequest = {
    reportNumber: record.reportNumber,
    timestamp: record.timestamp,
    encryptedPayload,
    metadata: {
      deviceType: getDeviceType(),
      os: navigator.platform,
      appVersion: '1.0.0'
    }
  };

  try {
    // 使用 fetch 发送请求
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    const response = await fetch(`${API_CONFIG.baseUrl}/api/v1/emergency/report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-App-Version': '1.0.0',
        'X-Request-ID': record.id
      },
      body: JSON.stringify(request),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        message: data.message || '求助已成功发送'
      };
    } else {
      // 服务器返回错误
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || `服务器错误: ${response.status}`
      };
    }
  } catch (error) {
    // 网络错误
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          success: false,
          message: '请求超时，请检查网络连接'
        };
      }
      return {
        success: false,
        message: `网络错误: ${error.message}`
      };
    }
    return {
      success: false,
      message: '未知错误'
    };
  }
}

/**
 * 使用Background Sync进行后台同步
 * 当网络恢复时自动重试
 */
export async function registerBackgroundSync(recordId: string): Promise<boolean> {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      // @ts-expect-error Background Sync API 类型
      await registration.sync.register(`sync-sos-${recordId}`);
      return true;
    } catch (error) {
      console.error('[同步] Background Sync注册失败:', error);
      return false;
    }
  }
  return false;
}

/**
 * 尝试同步所有待同步的记录
 */
export async function syncPendingRecords(
  records: SOSRecord[],
  onProgress?: (current: number, total: number) => void,
  onRecordSync?: (record: SOSRecord, success: boolean) => void
): Promise<{ synced: number; failed: number }> {
  let synced = 0;
  let failed = 0;

  for (let i = 0; i < records.length; i++) {
    const record = records[i];

    onProgress?.(i + 1, records.length);

    const result = await reportEmergency(record);

    if (result.success) {
      synced++;
    } else {
      failed++;
    }

    onRecordSync?.(record, result.success);
  }

  return { synced, failed };
}

/**
 * 获取设备类型
 */
function getDeviceType(): 'mobile' | 'desktop' {
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'mobile', 'windows phone'];

  for (const keyword of mobileKeywords) {
    if (userAgent.includes(keyword)) {
      return 'mobile';
    }
  }

  return 'desktop';
}

/**
 * 检查网络连接状态
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * 监听网络状态变化
 */
export function onNetworkChange(callback: (online: boolean) => void): () => void {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

/**
 * 获取当前网络类型（如果可用）
 */
export function getNetworkType(): string {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;

  if (connection) {
    return connection.effectiveType || 'unknown';
  }

  return 'unknown';
}

interface NetworkInformation {
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}
